import React, { useState, useMemo } from 'react';
import { C, R, SHADOW, fmt } from '../tokens.js';
import { D } from '../mock-data.js';
import { Card, Btn, BkHdr, Badge, SPill } from '../components/ui.jsx';
import { nextDays, dayAbbrev, isoDate, deliveriesOn, deliveryCountOn } from '../lib/schedule.js';
import { skipDay, unskipDay, skipDatesAllActive } from '../api/subscriptions.js';
import { humanizeError } from '../lib/errors.js';

const branchRatingFor = (branchId) => D.branches.grocery.find((b) => b.id === branchId)?.rating;

/* ── Date chip (single + multi-select) ──────────────────────── */
const DateChip = ({ date, active, selected, count, onTap, mode }) => {
  const isToday = new Date().toDateString() === date.toDateString();
  const dim = mode === 'single' ? !active : false;
  return (
    <button onClick={onTap} style={{
      flexShrink: 0, width: 60, padding: '10px 6px',
      borderRadius: R.lg,
      border: `1.5px solid ${selected ? C.primary : active ? C.primary : C.border}`,
      background: selected ? C.primary : active ? '#e6f7fb' : C.white,
      color: selected ? C.white : active ? C.primary : C.textMed,
      cursor: 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      transition: 'all .15s',
      scrollSnapAlign: 'start',
      opacity: dim ? 0.7 : 1,
      position: 'relative',
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, opacity: 0.85 }}>{dayAbbrev(date).toUpperCase()}</span>
      <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.3 }}>{date.getDate()}</span>
      {isToday && !selected && <span style={{ fontSize: 9, fontWeight: 800, color: selected ? C.white : C.primary, letterSpacing: 0.3, marginTop: -2 }}>TODAY</span>}
      {count > 0 && (
        <span style={{
          position: 'absolute', top: 4, right: 4,
          minWidth: 14, height: 14, padding: '0 4px',
          borderRadius: 7,
          background: selected ? C.white : C.primary,
          color: selected ? C.primary : C.white,
          fontSize: 9, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}>{count}</span>
      )}
    </button>
  );
};

/* ── Routine view (default Subs sub-tab) ────────────────────── */
const RoutineView = ({ subs, setSubscriptions, navigate }) => {
  const days = useMemo(() => nextDays(new Date(), 14), []);
  const [selectedIso, setSelectedIso] = useState(isoDate(days[0]));
  const [multiMode, setMultiMode] = useState(false);
  const [multiSel, setMultiSel] = useState(new Set());
  const [toast, setToast] = useState(null);

  const selectedDate = useMemo(() => {
    const d = days.find(d => isoDate(d) === selectedIso);
    return d || days[0];
  }, [days, selectedIso]);

  const deliveries = deliveriesOn(selectedDate, subs);

  // Optimistic skip / unskip.
  const setSubField = (subId, updater) => {
    setSubscriptions(prev => prev.map(s => s.id === subId ? updater(s) : s));
  };
  const applyDateToSub = (subId, iso, addOrRemove) => {
    setSubField(subId, (s) => {
      const cur = Array.isArray(s.skippedDates) ? s.skippedDates : [];
      const next = addOrRemove === 'add' ? (cur.includes(iso) ? cur : [...cur, iso]) : cur.filter(d => d !== iso);
      return { ...s, skippedDates: next };
    });
  };

  const toggleSkip = async (delivery) => {
    const iso = isoDate(selectedDate);
    const wasSkipped = delivery.skipped;
    applyDateToSub(delivery.subscriptionId, iso, wasSkipped ? 'remove' : 'add');
    try {
      if (wasSkipped) await unskipDay(delivery.subscriptionId, iso);
      else await skipDay(delivery.subscriptionId, iso);
    } catch (err) {
      // Revert on failure
      applyDateToSub(delivery.subscriptionId, iso, wasSkipped ? 'add' : 'remove');
      setToast(humanizeError(err));
      setTimeout(() => setToast(null), 3500);
    }
  };

  const onDateTap = (d) => {
    if (multiMode) {
      const iso = isoDate(d);
      const next = new Set(multiSel);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      setMultiSel(next);
    } else {
      setSelectedIso(isoDate(d));
    }
  };

  const cancelMultiMode = () => { setMultiMode(false); setMultiSel(new Set()); };

  const applyVacation = async () => {
    const dates = Array.from(multiSel).sort();
    if (dates.length === 0) return;
    // Estimate affected count for the confirm prompt.
    let affected = 0;
    for (const iso of dates) {
      const d = new Date(iso);
      affected += deliveriesOn(d, subs).filter(r => !r.skipped).length;
    }
    if (!window.confirm(`Skip ${affected} deliver${affected===1?'y':'ies'} across ${dates.length} day${dates.length>1?'s':''}?`)) return;
    // Optimistic local apply
    setSubscriptions(prev => prev.map(s => {
      if (s.status !== 'active') return s;
      const cur = Array.isArray(s.skippedDates) ? s.skippedDates : [];
      const adds = dates.filter(iso => {
        // Re-check: only add if this sub actually fires on that date
        const d = new Date(iso);
        // Inline fires check to avoid circular import; mirror subFiresOn semantics
        if (cur.includes(iso)) return false;
        const cad = s.cadence;
        if (cad === 'Daily') return true;
        if (cad === 'Weekly') return dayAbbrev(d) === (s.weeklyDay || 'Mon');
        if (cad === 'Custom') return !!(s.customDays && s.customDays[dayAbbrev(d)]);
        if (cad === 'Alt-day') {
          const anchor = s.anchorDate ? new Date(s.anchorDate) : new Date();
          const diff = Math.round((d.setHours(0,0,0,0) - new Date(anchor).setHours(0,0,0,0)) / 86_400_000);
          return Math.abs(diff) % 2 === 0;
        }
        return false;
      });
      if (adds.length === 0) return s;
      return { ...s, skippedDates: [...cur, ...adds] };
    }));
    cancelMultiMode();
    try {
      const r = await skipDatesAllActive(dates);
      setToast(`Paused ${r.affectedDeliveryCount} deliver${r.affectedDeliveryCount===1?'y':'ies'} · ${r.affectedSubscriptionCount} subscription${r.affectedSubscriptionCount===1?'':'s'}`);
      setTimeout(() => setToast(null), 3500);
    } catch (err) {
      setToast(humanizeError(err));
      setTimeout(() => setToast(null), 3500);
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Date strip + mode toggle */}
      <div style={{ padding: '4px 0 12px', borderBottom: `1px solid ${C.border}`, background: C.bg, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px 10px' }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: C.textMed, letterSpacing: 0.6 }}>
            {multiMode ? `${multiSel.size} day${multiSel.size===1?'':'s'} selected` : 'TAP A DAY'}
          </p>
          <button onClick={() => multiMode ? cancelMultiMode() : setMultiMode(true)} style={{ background: 'none', border: 'none', color: C.primary, fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '4px 6px', margin: '-4px -6px', letterSpacing: 0.1 }}>
            {multiMode ? 'Cancel' : 'Select dates'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 16px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {days.map((d) => {
            const iso = isoDate(d);
            return (
              <DateChip
                key={iso}
                date={d}
                active={!multiMode && iso === selectedIso}
                selected={multiMode && multiSel.has(iso)}
                count={deliveryCountOn(d, subs)}
                onTap={() => onDateTap(d)}
                mode={multiMode ? 'multi' : 'single'}
              />
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!multiMode && (
          <>
            <p style={{ fontSize: 11, fontWeight: 800, color: C.textMed, letterSpacing: 0.6, padding: '0 2px' }}>
              {selectedDate.toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' }).toUpperCase()}
              {' · '}
              {deliveries.filter(d => !d.skipped).length} deliver{deliveries.filter(d => !d.skipped).length === 1 ? 'y' : 'ies'}
            </p>
            {deliveries.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ width: 72, height: 72, borderRadius: 36, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 12, marginLeft: 'auto', marginRight: 'auto' }}>🌤</div>
                <p style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>No deliveries scheduled</p>
                <p style={{ fontSize: 12, color: C.textMed, marginTop: 4, lineHeight: 1.4 }}>Enjoy your day off.</p>
              </div>
            )}
            {(() => {
              // Group same-day deliveries by vendor for transparency.
              const byVendor = new Map();
              for (const d of deliveries) {
                if (!byVendor.has(d.branchId)) byVendor.set(d.branchId, { branchId: d.branchId, branchName: d.branchName, items: [] });
                byVendor.get(d.branchId).items.push(d);
              }
              const groups = Array.from(byVendor.values());
              const multiVendor = groups.length >= 2;
              return groups.map((g) => (
                <React.Fragment key={g.branchId}>
                  {multiVendor && (
                    <button onClick={() => { const full = D.branches.grocery.find((x) => x.id === g.branchId); if (full) navigate('branch', { branch: full, btype: 'grocery' }); }}
                      style={{ background: 'none', border: 'none', padding: '0 2px', margin: '4px 0 0', cursor: 'pointer', textAlign: 'left', display: 'block' }}>
                      <p style={{ fontSize: 11, fontWeight: 800, color: C.textMed, letterSpacing: 0.4 }}>
                        🏬 {g.branchName}{branchRatingFor(g.branchId) ? ` · ⭐${branchRatingFor(g.branchId)}` : ''} <span style={{ color: C.primary }}>· View →</span>
                      </p>
                    </button>
                  )}
                  {g.items.map((d) => {
                    const sourceSub = subs.find((s) => s.id === d.subscriptionId);
                    return (
                      <Card key={d.subscriptionId} style={{ opacity: d.skipped ? 0.7 : 1, transition: 'opacity .15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 44, height: 44, borderRadius: R.lg, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, lineHeight: 1 }}>{d.productEm}</div>
                          <div onClick={() => sourceSub && navigate('sub-detail', { sub: sourceSub })} style={{ flex: 1, minWidth: 0, cursor: sourceSub ? 'pointer' : 'default' }}>
                            <p style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: -0.1, lineHeight: 1.3, textDecoration: d.skipped ? 'line-through' : 'none' }}>{d.productName}</p>
                            <p style={{ fontSize: 11, color: C.textMed, marginTop: 2, lineHeight: 1.4 }}>{d.qty} × {d.variantLabel}{multiVendor ? '' : ` · ${d.branchName}${branchRatingFor(d.branchId) ? ` · ⭐${branchRatingFor(d.branchId)}` : ''}`}</p>
                            <p style={{ fontSize: 11, color: C.textMed, marginTop: 2, lineHeight: 1.4 }}>⏰ {d.deliveryWindow} {sourceSub && <span style={{ color: C.primary, fontWeight: 700 }}>· Modify →</span>}</p>
                          </div>
                          <button onClick={() => toggleSkip(d)} style={{
                            flexShrink: 0,
                            padding: '8px 12px', borderRadius: R.sm,
                            border: `1.5px solid ${d.skipped ? C.primary : C.error}`,
                            background: d.skipped ? '#e6f7fb' : C.white,
                            color: d.skipped ? C.primary : C.error,
                            fontSize: 11, fontWeight: 800, cursor: 'pointer',
                            minHeight: 34, minWidth: 80, letterSpacing: 0.1,
                          }}>{d.skipped ? 'Skipped · Undo' : 'Skip today'}</button>
                        </div>
                      </Card>
                    );
                  })}
                </React.Fragment>
              ));
            })()}
          </>
        )}
        {multiMode && multiSel.size === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 12, marginLeft: 'auto', marginRight: 'auto' }}>🏖</div>
            <p style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>Going away?</p>
            <p style={{ fontSize: 12, color: C.textMed, marginTop: 4, lineHeight: 1.4, maxWidth: 260, marginLeft: 'auto', marginRight: 'auto' }}>Tap the days you'll be away. We'll pause all deliveries for those dates.</p>
          </div>
        )}
        {multiMode && multiSel.size > 0 && (
          <Card>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.textMed, marginBottom: 10, letterSpacing: 0.1 }}>Selected days</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Array.from(multiSel).sort().map(iso => {
                const d = new Date(iso);
                return (
                  <Badge key={iso} bg="#e6f7fb" color={C.primary}>{dayAbbrev(d)} {d.getDate()} {d.toLocaleString('en-IN',{month:'short'})}</Badge>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Vacation action bar */}
      {multiMode && multiSel.size > 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 16px 16px', background: C.white, borderTop: `1px solid ${C.border}`, boxShadow: SHADOW.bottomBar, display: 'flex', gap: 10 }}>
          <button onClick={cancelMultiMode} style={{ padding: '12px 14px', borderRadius: R.lg, border: `1.5px solid ${C.border}`, background: C.white, color: C.textMed, fontSize: 13, fontWeight: 800, cursor: 'pointer', minHeight: 48 }}>Cancel</button>
          <Btn full onClick={applyVacation} danger>Pause {multiSel.size} day{multiSel.size>1?'s':''}</Btn>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="slide-up" style={{ position: 'absolute', bottom: 80, left: 16, right: 16, padding: '12px 16px', background: '#1c1917', color: C.white, borderRadius: R.lg, fontSize: 13, fontWeight: 700, boxShadow: SHADOW.raised, zIndex: 30 }}>
          {toast}
        </div>
      )}
    </div>
  );
};

/* ── All-subs view (existing list, demoted to sub-tab) ──────── */
const AllSubsView = ({ subs, navigate }) => {
  const [tab, setTab] = useState('active');
  const shown = subs.filter(s => s.status === tab);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 16px 0', background: C.bg }}>
        <div style={{ display: 'flex', background: '#f5f5f4', borderRadius: R.lg, padding: 4, marginBottom: 12, gap: 4 }}>
          {['active', 'paused', 'cancelled'].map(t => {
            const on = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '9px 4px', borderRadius: R.md, border: 'none', background: on ? C.white : 'transparent', color: on ? C.primary : C.textMed, fontSize: 12, fontWeight: 800, cursor: 'pointer', boxShadow: on ? '0 1px 3px rgba(28,25,23,.08)' : 'none', transition: 'all .18s', letterSpacing: 0.1 }}>
                {t.charAt(0).toUpperCase() + t.slice(1)} ({subs.filter(s => s.status === t).length})
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map(sub => (
          <Card key={sub.id} onClick={() => navigate('sub-detail', { sub })} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 10 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: -0.2, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.product}</p>
                <p style={{ fontSize: 11, color: C.textMed, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.branchName} · {sub.variant}</p>
              </div>
              <SPill status={sub.status} />
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              <Badge bg="#f5f5f4" color={C.textMed} sm>📅 {sub.cadence}</Badge>
              <Badge bg="#f5f5f4" color={C.textMed} sm>⏰ {sub.window}</Badge>
              <Badge bg="#f5f5f4" color={C.textMed} sm>× {sub.qty}</Badge>
            </div>
            {sub.status === 'active' && <p style={{ fontSize: 12, color: C.textMed }}>Next · <b style={{ color: C.text }}>{sub.nextDel}</b></p>}
            <p style={{ fontSize: 13, fontWeight: 800, color: C.primary, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{fmt(sub.priceP)} / delivery</p>
          </Card>
        ))}
        {shown.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 12, marginLeft: 'auto', marginRight: 'auto' }}>📅</div>
            <p style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>No {tab} subscriptions</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Outer screen with sub-tabs ─────────────────────────────── */
export const SubscriptionsScreen = ({ navigate, subs = [], setSubscriptions }) => {
  const [view, setView] = useState('routine');
  return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '20px 16px 0', background: C.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>Your routine</h2>
          <button onClick={() => navigate('product-catalog')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: R.pill, border: `1.5px solid ${C.primary}`, background: C.white, color: C.primary, fontSize: 12, fontWeight: 800, cursor: 'pointer', letterSpacing: 0.1, minHeight: 34 }}>+ Add</button>
        </div>
        <div style={{ display: 'flex', background: '#f5f5f4', borderRadius: R.lg, padding: 4, marginBottom: 0, gap: 4 }}>
          {[{ id: 'routine', l: 'Routine' }, { id: 'all', l: 'All subs' }].map(v => {
            const on = view === v.id;
            return (
              <button key={v.id} onClick={() => setView(v.id)} style={{ flex: 1, padding: '10px 12px', borderRadius: R.md, border: 'none', background: on ? C.white : 'transparent', color: on ? C.primary : C.textMed, fontSize: 13, fontWeight: 800, cursor: 'pointer', boxShadow: on ? '0 1px 3px rgba(28,25,23,.08)' : 'none', transition: 'all .18s', letterSpacing: 0.1 }}>{v.l}</button>
            );
          })}
        </div>
      </div>
      {subs.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
          <div style={{ width: 96, height: 96, borderRadius: 48, background: '#e6f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, marginBottom: 20 }}>📅</div>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: -0.2 }}>No subscriptions yet</h3>
          <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.5, maxWidth: 280, marginBottom: 24 }}>Set up your first routine — milk, bread, eggs delivered before you wake up.</p>
          <Btn onClick={() => navigate('product-catalog')}>Browse products</Btn>
        </div>
      ) : view === 'routine' ? (
        <RoutineView subs={subs} setSubscriptions={setSubscriptions} navigate={navigate} />
      ) : (
        <AllSubsView subs={subs} navigate={navigate} />
      )}
    </div>
  );
};

/* ── Subscription detail (unchanged behaviour) ──────────────── */
export const SubDetailScreen = ({ sub, goBack, navigate }) => {
  const [status, setStatus] = useState(sub.status);
  const [showPause, setShowPause] = useState(false);
  const [resumeDate, setResumeDate] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  if (cancelled) return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <BkHdr title="Subscription" onBack={goBack} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
        <div style={{ width: 96, height: 96, borderRadius: 48, background: C.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, marginBottom: 20 }}>⛔</div>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: -0.2 }}>Subscription cancelled</h3>
        <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.5 }}>Your {sub.product} subscription has been cancelled.</p>
        <button onClick={goBack} style={{ marginTop: 24, background: 'none', border: 'none', color: C.primary, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>← Back</button>
      </div>
    </div>
  );
  return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <BkHdr title="Subscription detail" onBack={goBack} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.3, lineHeight: 1.25 }}>{sub.product}</p>
              <button onClick={() => { const full = D.branches.grocery.find((x) => x.id === sub.branchId); if (full && navigate) navigate('branch', { branch: full, btype: 'grocery' }); }}
                style={{ background: 'none', border: 'none', padding: 0, margin: '3px 0 0', cursor: navigate ? 'pointer' : 'default', textAlign: 'left' }}>
                <p style={{ fontSize: 13, color: C.textMed }}>
                  {sub.branchName} · {sub.variant} {navigate && <span style={{ color: C.primary, fontWeight: 700 }}>· View store →</span>}
                </p>
              </button>
            </div>
            <SPill status={status} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Badge bg="#e6f7fb" color={C.primary}>📅 {sub.cadence}</Badge>
            <Badge bg="#e6f7fb" color={C.primary}>⏰ {sub.window}</Badge>
            <Badge bg="#e6f7fb" color={C.primary}>× {sub.qty}</Badge>
            <Badge bg="#e6f7fb" color={C.primary}>{fmt(sub.priceP)}/delivery</Badge>
          </div>
        </Card>
        {status === 'active' && !skipped && (
          <Card style={{ background: '#dcfce7', border: '1px solid #86efac', boxShadow: 'none' }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: '#15803d', marginBottom: 4, letterSpacing: 0.4 }}>NEXT DELIVERY</p>
            <p style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>{sub.nextDel}</p>
            <p style={{ fontSize: 12, color: '#16a34a', marginTop: 4, lineHeight: 1.4 }}>{sub.qty} × {sub.variant} · {sub.window}</p>
          </Card>
        )}
        {skipped && (
          <div style={{ padding: '12px 14px', background: C.warningBg, borderRadius: R.lg, border: `1px solid #fde68a` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#92400e' }}>Next delivery skipped ✓</p>
          </div>
        )}
        {showPause && (
          <Card>
            <p style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 10, letterSpacing: -0.1 }}>Pause until when?</p>
            <input type="date" value={resumeDate} onChange={e => setResumeDate(e.target.value)} style={{ width: '100%', padding: '12px 14px', borderRadius: R.lg, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, outline: 'none', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setStatus('paused'); setShowPause(false); }} disabled={!resumeDate} style={{ flex: 1, padding: '11px', borderRadius: R.md, border: 'none', background: resumeDate ? C.warning : '#d4f1f7', color: resumeDate ? C.white : C.textLight, fontSize: 13, fontWeight: 800, cursor: resumeDate ? 'pointer' : 'not-allowed', minHeight: 42 }}>Confirm pause</button>
              <button onClick={() => setShowPause(false)} style={{ flex: 1, padding: '11px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.textMed, fontSize: 13, fontWeight: 800, cursor: 'pointer', minHeight: 42 }}>Cancel</button>
            </div>
          </Card>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {status === 'active' && <button onClick={() => setShowPause(true)} style={{ padding: '14px', borderRadius: R.lg, border: `1.5px solid ${C.warning}`, background: '#fef3c7', color: C.warning, fontSize: 14, fontWeight: 800, cursor: 'pointer', minHeight: 48 }}>⏸ Pause subscription</button>}
          {status === 'paused' && <button onClick={() => setStatus('active')} style={{ padding: '14px', borderRadius: R.lg, border: `1.5px solid ${C.success}`, background: '#dcfce7', color: C.success, fontSize: 14, fontWeight: 800, cursor: 'pointer', minHeight: 48 }}>▶ Resume subscription</button>}
          {status === 'active' && !skipped && <button onClick={() => { if (window.confirm('Skip the next delivery?')) setSkipped(true); }} style={{ padding: '14px', borderRadius: R.lg, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 14, fontWeight: 800, cursor: 'pointer', minHeight: 48 }}>⏭ Skip next delivery</button>}
          <button onClick={() => { if (window.confirm('Cancel this subscription permanently?')) setCancelled(true); }} style={{ padding: '14px', borderRadius: R.lg, border: `1.5px solid ${C.error}`, background: 'transparent', color: C.error, fontSize: 14, fontWeight: 800, cursor: 'pointer', minHeight: 48 }}>✕ Cancel subscription</button>
        </div>
        <Card>
          <p style={{ fontSize: 11, fontWeight: 800, color: C.textMed, marginBottom: 12, letterSpacing: 0.6 }}>RECENT DELIVERIES</p>
          {[{ date: '25 May', status: 'delivered' }, { date: '24 May', status: 'delivered' }, { date: '23 May', status: 'delivered' }].map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{sub.product}</p>
                <p style={{ fontSize: 11, color: C.textMed }}>{d.date} · {sub.window}</p>
              </div>
              <SPill status={d.status} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};
